import React from "react";
import { useApp } from "../../contexts/AppContext";
import { TEMARIO } from "../../constants/temario";
import { siteConfig } from "../../config/siteConfig";

const Sidebar = ({ isOpen, setIsOpen }) => {
  const {
    view,
    setView,
    setCurrentExam,
    navigateToProtectedFeature,
    isPremium,
  } = useApp();

  const { brand, exam, uiCopy } = siteConfig;

  const shuffleArray = (array) => {
    const newArr = [...array];
    for (let i = newArr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
    }
    return newArr;
  };

  const handleSectionChange = (sectionId) => {
    setView(sectionId);
    if (window.innerWidth < 1024) setIsOpen(false);
  };

  const handleSimulatorClick = () => {
    if (!isPremium) {
      navigateToProtectedFeature("exam");
      return;
    }

    const TOTAL_QUESTIONS = exam.simulatorQuestions;
    const TOPIC_WEIGHTS = exam.topicWeights;

    let selectedQuestions = [];

    TEMARIO.forEach((topic) => {
      const config = TOPIC_WEIGHTS.find((t) => t.id === topic.id);
      const weight = config ? config.weight : 0.05;
      const countForTopic = Math.round(TOTAL_QUESTIONS * weight);

      const shuffledTopicQs = shuffleArray([...topic.questions]);
      selectedQuestions.push(
        ...shuffledTopicQs.slice(0, Math.min(countForTopic, shuffledTopicQs.length))
      );
    });

    if (selectedQuestions.length > TOTAL_QUESTIONS) {
      selectedQuestions = selectedQuestions.slice(0, TOTAL_QUESTIONS);
    } else {
      const allQs = TEMARIO.flatMap((t) => t.questions);
      while (selectedQuestions.length < TOTAL_QUESTIONS) {
        selectedQuestions.push(allQs[Math.floor(Math.random() * allQs.length)]);
      }
    }

    const mixedQuestions = shuffleArray([...selectedQuestions]);

    const freshQuestions = mixedQuestions.map((q) => {
      const correctText = q.o[q.c];
      const shuffledOptions = shuffleArray([...q.o]);
      const newCorrectIndex = shuffledOptions.indexOf(correctText);

      return {
        q: q.q,
        o: shuffledOptions,
        c: newCorrectIndex,
        exp: q.exp,
        answered: false,
        selectedAnswer: null,
      };
    });

    const simulatorExam = {
      title: exam.simulatorTitle,
      questions: freshQuestions,
      sourceId: "simulator",
      currentIdx: 0,
      score: 0,
      timer: 0,
    };

    setCurrentExam(null);
    setTimeout(() => {
      setCurrentExam(simulatorExam);
      setView("exam");
      if (window.innerWidth < 1024) setIsOpen(false);
    }, 10);
  };

  return (
    <aside
      className={`
      fixed inset-y-0 left-0 z-[55] w-72 bg-white border-r flex flex-col transition-transform duration-300 ease-in-out
      lg:static lg:translate-x-0
      ${isOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}
    `}
    >
      <div className="p-6 border-b flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-blue-600 p-2 rounded-xl text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="flex flex-col leading-none">
            <span className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
              {brand.namePrefix}
              <span className="text-blue-600">{brand.nameSuffix}</span>
            </span>
            {brand.subBrand && (
              <span className="text-[12px] font-bold text-blue-600 mt-0.5 tracking-tighter">
                {brand.subBrand}
              </span>
            )}
          </div>
        </div>

        <button onClick={() => setIsOpen(false)} className="lg:hidden p-2 text-slate-400 hover:text-slate-600">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <nav className="flex-grow py-6 px-4 space-y-2 overflow-y-auto custom-scrollbar">
        <p className="px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
          {uiCopy.sidebarPrincipal}
        </p>

        <button
          onClick={() => handleSectionChange("dashboard")}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
            view === "dashboard" ? "bg-blue-50 text-blue-600" : "text-slate-500 hover:bg-slate-50"
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          {uiCopy.navHome}
        </button>

        <button
          onClick={() => handleSectionChange("study")}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
            view === "study" ? "bg-blue-50 text-blue-600" : "text-slate-500 hover:bg-slate-50"
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          {uiCopy.navStudyShort}
        </button>

        <button
          onClick={() => handleSectionChange("quizzes")}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
            view === "quizzes" ? "bg-blue-50 text-blue-600" : "text-slate-500 hover:bg-slate-50"
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          {uiCopy.navQuizzesShort}
        </button>

        <div className="pt-6">
          <p className="px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
            {uiCopy.sidebarSimulation}
          </p>
          <button
            onClick={handleSimulatorClick}
            className="w-full group bg-gradient-to-r from-blue-600 to-indigo-700 text-white flex items-center gap-3 px-4 py-4 rounded-2xl text-sm font-bold shadow-lg shadow-blue-200 transition-all hover:scale-[1.02] active:scale-95"
          >
            <svg className="w-6 h-6 text-blue-200 group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {uiCopy.navSimulator}
            {!isPremium && <span className="ml-auto text-xs opacity-60">🔒</span>}
          </button>
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;
